import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { SESSION_TOKEN_KEY, POLL_INTERVAL } from './utils';

interface Options {
  /** Chamado quando a sessão atual foi invalidada (login em outro device). */
  onMismatch: (message?: string) => void | Promise<void>;
}

/**
 * Watcher de sessão única — combina realtime em `active_sessions` + polling fallback
 * e revalidação ao retomar foco/visibilidade.
 *
 * Retorna start/stop/check para o provider controlar o ciclo conforme o usuário loga/desloga.
 */
export function useSingleSessionPoll({ onMismatch }: Options) {
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const stop = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  const checkToken = useCallback(
    async (userId: string) => {
      const localToken = localStorage.getItem(SESSION_TOKEN_KEY);
      if (!localToken) return;
      try {
        const { data, error } = await supabase
          .from('active_sessions')
          .select('session_token')
          .eq('user_id', userId)
          .maybeSingle();
        if (error) return;
        if (!data || data.session_token !== localToken) {
          await onMismatch(
            'Sua sessão foi encerrada porque outro dispositivo fez login com esta conta.',
          );
        }
      } catch {
        // transient
      }
    },
    [onMismatch],
  );

  const start = useCallback(
    (userId: string) => {
      stop();
      const channel = supabase
        .channel(`active-session-${userId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'active_sessions',
            filter: `user_id=eq.${userId}`,
          },
          (payload: any) => {
            const newToken = payload?.new?.session_token;
            const localToken = localStorage.getItem(SESSION_TOKEN_KEY);
            if (!localToken) return;
            if (payload.eventType === 'DELETE' || (newToken && newToken !== localToken)) {
              onMismatch(
                'Sua sessão foi encerrada porque outro dispositivo fez login com esta conta.',
              );
            }
          },
        )
        .subscribe();
      channelRef.current = channel;

      pollRef.current = setInterval(() => {
        checkToken(userId);
      }, POLL_INTERVAL);
    },
    [stop, checkToken, onMismatch],
  );

  // Revalidação em foco/visibilidade
  useEffect(() => {
    const handleFocus = () => {
      const storedToken = localStorage.getItem(SESSION_TOKEN_KEY);
      supabase.auth.getSession().then(({ data: { session: s } }) => {
        if (s?.user && storedToken) checkToken(s.user.id);
      });
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') handleFocus();
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [checkToken]);

  return { start, stop, checkToken };
}
